import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { firestoreTimeToMillis } from '../utils/firestoreTime'
import { submitFormResponse } from './submissionService'
import { logAuditEvent, snapshotAuditTrail } from './auditLedgerService'
import { assertWorkspaceQuota } from './quotaGuardService'
import { normalizeTierId } from '../config/PricingConfig'
import { limitsLiftedForWorkspace } from '../utils/demoUnlock'
import {
  buildTitlePrefixes,
  normalizeFormTitle,
  validateFormDefinitionDraft,
  type FormDefinition,
  type FormDefinitionDraft,
  type FormLifecycleStatus,
  type FormProviderExportRequest,
  type FormResponse,
} from '../types/forms'

export const FORM_PAGE_SIZE = 20

export const definitionFromSnapshot = (snapshot: QueryDocumentSnapshot<DocumentData>): FormDefinition => {
  const data = snapshot.data()
  return {
    id: snapshot.id,
    ...data,
    titleSearch: typeof data.titleSearch === 'string' ? data.titleSearch : normalizeFormTitle(String(data.title ?? '')),
    titlePrefixes: Array.isArray(data.titlePrefixes) ? data.titlePrefixes : [],
    submissionCount: typeof data.submissionCount === 'number' ? data.submissionCount : 0,
  } as FormDefinition
}

export const subscribeRestrictedFormDefinitions = (
  workspaceId: string,
  uid: string,
  onChange: (definitions: FormDefinition[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  const colRef = collection(firestoreDb, 'form_definitions')
  const qScope = query(colRef, where('workspaceId', '==', workspaceId), where('scopeRead', 'array-contains', uid))
  const qRecipients = query(colRef, where('workspaceId', '==', workspaceId), where('requestRecipientUids', 'array-contains', uid))
  const qCreated = query(colRef, where('workspaceId', '==', workspaceId), where('createdBy', '==', uid))

  let answeredScope = false
  let answeredRecipients = false
  let answeredCreated = false

  const mapScope = new Map<string, FormDefinition>()
  const mapRecipients = new Map<string, FormDefinition>()
  const mapCreated = new Map<string, FormDefinition>()

  const emit = () => {
    if (!answeredScope || !answeredRecipients || !answeredCreated) return
    const merged = new Map<string, FormDefinition>()
    for (const [id, def] of mapScope) merged.set(id, def)
    for (const [id, def] of mapRecipients) merged.set(id, def)
    for (const [id, def] of mapCreated) merged.set(id, def)
    onChange(Array.from(merged.values()))
  }

  const handleError = (error: unknown) => {
    const err = error instanceof Error ? error : new Error(String(error))
    onError?.(err)
  }

  const unsubs: Unsubscribe[] = [
    onSnapshot(qScope, (snap) => {
      answeredScope = true
      mapScope.clear()
      for (const d of snap.docs) {
        mapScope.set(d.id, definitionFromSnapshot(d))
      }
      emit()
    }, handleError),
    onSnapshot(qRecipients, (snap) => {
      answeredRecipients = true
      mapRecipients.clear()
      for (const d of snap.docs) {
        mapRecipients.set(d.id, definitionFromSnapshot(d))
      }
      emit()
    }, handleError),
    onSnapshot(qCreated, (snap) => {
      answeredCreated = true
      mapCreated.clear()
      for (const d of snap.docs) {
        mapCreated.set(d.id, definitionFromSnapshot(d))
      }
      emit()
    }, handleError),
  ]

  return () => {
    for (const unsub of unsubs) unsub()
  }
}

type MutationActor = { id: string; name: string }

const isFirestoreTimestampLike = (value: object) => 'toDate' in value && typeof (value as { toDate?: unknown }).toDate === 'function'

const stripUndefinedValues = <T>(value: T): T => {
  if (Array.isArray(value)) return value.map((entry) => entry === undefined ? null : stripUndefinedValues(entry)) as T
  if (!value || typeof value !== 'object' || value instanceof Date || isFirestoreTimestampLike(value)) return value
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .map(([key, entry]) => [key, stripUndefinedValues(entry)]),
  ) as T
}

export const getFormDefinition = async (formId: string) => {
  const snapshot = await getDoc(doc(firestoreDb, 'form_definitions', formId))
  return snapshot.exists() ? ({ id: snapshot.id, ...snapshot.data() } as FormDefinition) : null
}

export const listActiveWorkspaceForms = async (workspaceId: string): Promise<FormDefinition[]> => {
  const colRef = collection(firestoreDb, 'form_definitions')
  const q = query(colRef, where('workspaceId', '==', workspaceId), where('lifecycleStatus', '==', 'ACTIVE'))
  const snap = await getDocs(q)
  return snap.docs
    .map(definitionFromSnapshot)
    .filter((f) => !f.isLocked)
}

export const saveFormDefinition = async (draft: FormDefinitionDraft, actor: MutationActor) => {
  const sanitizedDraft = stripUndefinedValues(draft)
  const errors = validateFormDefinitionDraft(sanitizedDraft)
  if (errors.length > 0) throw new Error(errors[0])
  const definitionRef = doc(firestoreDb, 'form_definitions', sanitizedDraft.id)
  const [existingDefinition, workspaceSnapshot] = await Promise.all([
    getDoc(definitionRef),
    getDoc(doc(firestoreDb, 'workspaces', sanitizedDraft.workspaceId)),
  ])
  if (!limitsLiftedForWorkspace(workspaceSnapshot.data()) && normalizeTierId(workspaceSnapshot.data()?.plan) === 'FREE_TIER' && sanitizedDraft.fields.some((field) => field.type === 'FILE_UPLOAD')) {
    throw new Error('Free workspaces cannot use form file-upload fields.')
  }
  if (!existingDefinition.exists()) await assertWorkspaceQuota(sanitizedDraft.workspaceId, 'FORM_CREATE')
  const created = await runTransaction(firestoreDb, async (transaction) => {
    const [current, workspace] = await Promise.all([transaction.get(definitionRef), transaction.get(doc(firestoreDb, 'workspaces', sanitizedDraft.workspaceId))])
    const currentData = current.data()
    transaction.set(definitionRef, {
      ...sanitizedDraft,
      title: sanitizedDraft.title.trim(),
      titleSearch: normalizeFormTitle(sanitizedDraft.title),
      titlePrefixes: buildTitlePrefixes(sanitizedDraft.title),
      submissionCount: typeof currentData?.submissionCount === 'number'
        ? currentData.submissionCount
        : sanitizedDraft.submissionCount ?? 0,
      lastSubmissionResponseId: typeof currentData?.lastSubmissionResponseId === 'string' ? currentData.lastSubmissionResponseId : null,
      createdAt: current.exists() ? currentData?.createdAt : serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true })
    logAuditEvent(firestoreDb, transaction, workspace.data(), {
      workspaceId: sanitizedDraft.workspaceId,
      actorId: actor.id,
      actorName: actor.name,
      nodeType: 'FORM_DEFINITION',
      targetId: sanitizedDraft.id,
      targetTitle: sanitizedDraft.title,
      action: 'SAVE_FORM',
      activityType: current.exists() ? 'UPDATE' : 'CREATE',
      summary: `${current.exists() ? 'Saved changes to' : 'Created'} form: ${sanitizedDraft.title}`,
      payload: { lifecycleStatus: sanitizedDraft.lifecycleStatus },
      snapshot: snapshotAuditTrail(currentData ?? null, sanitizedDraft as unknown as Record<string, unknown>, ['title', 'description', 'lifecycleStatus', 'deadline', 'isLocked']),
    })
    return !current.exists()
  })
  emitFormEconomyAction(created ? 'CREATE' : 'DESIGN', sanitizedDraft.workspaceId, actor.id)
  return sanitizedDraft.id
}

export const archiveFormDefinition = async (form: FormDefinition, actor: MutationActor) => {
  const definitionRef = doc(firestoreDb, 'form_definitions', form.id)
  await runTransaction(firestoreDb, async (transaction) => {
    const [current, workspace] = await Promise.all([transaction.get(definitionRef), transaction.get(doc(firestoreDb, 'workspaces', form.workspaceId))])
    if (!current.exists()) throw new Error('Form definition no longer exists.')
    transaction.update(definitionRef, { lifecycleStatus: 'ARCHIVED', isLocked: true, updatedAt: serverTimestamp() })
    logAuditEvent(firestoreDb, transaction, workspace.data(), {
      workspaceId: form.workspaceId,
      actorId: actor.id,
      actorName: actor.name,
      nodeType: 'FORM_DEFINITION',
      targetId: form.id,
      targetTitle: form.title,
      action: 'ARCHIVE_FORM',
      activityType: 'MOVE',
      summary: `Archived form: ${form.title}`,
      snapshot: snapshotAuditTrail(current.data(), { ...current.data(), lifecycleStatus: 'ARCHIVED', isLocked: true }, ['lifecycleStatus', 'isLocked']),
    })
  })
}

export const updateFormLifecycleStatus = async (form: FormDefinition, lifecycleStatus: FormLifecycleStatus, actor: MutationActor) => {
  const nextLocked = lifecycleStatus === 'ARCHIVED'
  const validationDraft: FormDefinitionDraft = {
    ...form,
    lifecycleStatus,
    isLocked: nextLocked,
  }
  const errors = validateFormDefinitionDraft(stripUndefinedValues(validationDraft))
  if (errors.length > 0) throw new Error(`Fix this before changing status: ${errors[0]}`)

  const definitionRef = doc(firestoreDb, 'form_definitions', form.id)
  await runTransaction(firestoreDb, async (transaction) => {
    const [current, workspace] = await Promise.all([
      transaction.get(definitionRef),
      transaction.get(doc(firestoreDb, 'workspaces', form.workspaceId)),
    ])
    if (!current.exists()) throw new Error('Form definition no longer exists.')
    const currentRaw = current.data()
    const currentData = currentRaw as FormDefinition
    const currentDraft: FormDefinitionDraft = {
      ...currentData,
      id: form.id,
      lifecycleStatus,
      isLocked: nextLocked,
    }
    const currentErrors = validateFormDefinitionDraft(stripUndefinedValues(currentDraft))
    if (currentErrors.length > 0) throw new Error(`Fix this before changing status: ${currentErrors[0]}`)
    transaction.update(definitionRef, { lifecycleStatus, isLocked: nextLocked, updatedAt: serverTimestamp() })
    logAuditEvent(firestoreDb, transaction, workspace.data(), {
      workspaceId: form.workspaceId,
      actorId: actor.id,
      actorName: actor.name,
      nodeType: 'FORM_DEFINITION',
      targetId: form.id,
      targetTitle: currentData.title ?? form.title,
      action: 'UPDATE_FORM_LIFECYCLE',
      activityType: 'UPDATE',
      summary: `Changed form status to ${lifecycleStatus}: ${currentData.title ?? form.title}`,
      payload: { lifecycleStatus },
      snapshot: snapshotAuditTrail(
        currentRaw,
        { ...currentRaw, lifecycleStatus, isLocked: nextLocked },
        ['lifecycleStatus', 'isLocked'],
      ),
    })
  })
}

export const deleteArchivedFormDefinition = async (form: FormDefinition, actor: MutationActor) => {
  const definitionRef = doc(firestoreDb, 'form_definitions', form.id)
  await runTransaction(firestoreDb, async (transaction) => {
    const [current, workspace] = await Promise.all([transaction.get(definitionRef), transaction.get(doc(firestoreDb, 'workspaces', form.workspaceId))])
    if (!current.exists()) return
    if (current.data().lifecycleStatus !== 'ARCHIVED') throw new Error('Only archived forms can be permanently deleted.')
    transaction.delete(definitionRef)
    logAuditEvent(firestoreDb, transaction, workspace.data(), {
      workspaceId: form.workspaceId,
      actorId: actor.id,
      actorName: actor.name,
      nodeType: 'FORM_DEFINITION',
      targetId: form.id,
      targetTitle: form.title,
      action: 'DELETE_FORM',
      activityType: 'DELETE',
      summary: `Deleted archived form definition: ${form.title}`,
      payload: { responsesRetained: true },
    })
  })
}

export const handleFormSubmission = async (input: {
  workspaceId: string
  form: FormDefinition
  submittedBy: string
  submitterName: string
  data: Record<string, unknown>
}): Promise<FormResponse> => {
  const result = await submitFormResponse({ definition: input.form, values: input.data, userId: input.submittedBy, userName: input.submitterName })
  if (!result.response) throw new Error(Object.values(result.errors)[0] ?? 'The response is invalid.')
  return result.response
}

export const exportResponseToProvider = async (request: FormProviderExportRequest, ready: boolean) => {
  if (!ready) throw new Error(`${request.providerId} must be linked before exporting responses.`)
  // TODO: Route the response through the provider-specific callable/API implementation.
  return { queued: false, providerId: request.providerId } as const
}

export const importDefinitionFromProvider = async (providerId: FormProviderExportRequest['providerId'], ready: boolean) => {
  if (!ready) throw new Error(`${providerId} must be linked before importing a definition.`)
  // TODO: Hydrate a FormDefinitionDraft from the provider-specific schema.
  return { imported: false, providerId } as const
}

export const shareForm = async (workspaceId: string, formId: string, actorId: string) => {
  void workspaceId
  void actorId
  // TODO: Persist a public share token after public-form access rules are designed.
  return { formId, shareUrl: null } as const
}

const emitFormEconomyAction = (action: 'CREATE' | 'DESIGN' | 'SUBMIT' | 'SHARE', workspaceId: string, actorId: string) => {
  void action
  void workspaceId
  void actorId
  // TODO: Bridge Forms actions into AppEconomyLoop after reward policy is ratified.
}

export const subscribeFormResponsesForForm = (
  workspaceId: string,
  formId: string,
  options: { submittedBy?: string; max?: number } = {},
  onChange: (responses: FormResponse[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  const colRef = collection(firestoreDb, 'form_responses')
  const constraints = [
    where('workspaceId', '==', workspaceId),
    where('formId', '==', formId),
    ...(options.submittedBy ? [where('submittedBy', '==', options.submittedBy)] : []),
    orderBy('submittedAt', 'desc'),
    limit(options.max ?? 200),
  ]
  const q = query(colRef, ...constraints)
  return onSnapshot(
    q,
    (snapshot) => {
      onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as FormResponse))
    },
    (error) => {
      onError?.(error instanceof Error ? error : new Error(String(error)))
    },
  )
}

export const subscribeWorkspaceFormResponses = (
  workspaceId: string,
  options: { submittedBy?: string; limitCount?: number } = {},
  onChange: (responses: FormResponse[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  const colRef = collection(firestoreDb, 'form_responses')
  const constraints = [
    where('workspaceId', '==', workspaceId),
    ...(options.submittedBy ? [where('submittedBy', '==', options.submittedBy)] : []),
    orderBy('submittedAt', 'desc'),
    limit(options.limitCount ?? 100),
  ]
  const q = query(colRef, ...constraints)
  return onSnapshot(
    q,
    (snapshot) => {
      onChange(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as FormResponse))
    },
    (error) => {
      onError?.(error instanceof Error ? error : new Error(String(error)))
    },
  )
}

export const subscribeResponsesForForms = (
  workspaceId: string,
  formIds: string[],
  onChange: (responses: FormResponse[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  const uniqueFormIds = Array.from(new Set(formIds.filter(Boolean)))
  if (uniqueFormIds.length === 0) {
    onChange([])
    return () => {}
  }

  const responsesByForm = new Map<string, FormResponse[]>()

  const emit = () => {
    const allResponses: FormResponse[] = []
    for (const formResponses of responsesByForm.values()) {
      allResponses.push(...formResponses)
    }
    allResponses.sort((a, b) => firestoreTimeToMillis(b.submittedAt) - firestoreTimeToMillis(a.submittedAt))
    const seen = new Set<string>()
    const deduplicated: FormResponse[] = []
    for (const resp of allResponses) {
      if (!seen.has(resp.id)) {
        seen.add(resp.id)
        deduplicated.push(resp)
      }
    }
    onChange(deduplicated.slice(0, 100))
  }

  const handleError = (error: unknown) => {
    const err = error instanceof Error ? error : new Error(String(error))
    onError?.(err)
  }

  const colRef = collection(firestoreDb, 'form_responses')
  const unsubs: Unsubscribe[] = uniqueFormIds.map((formId) => {
    const q = query(
      colRef,
      where('workspaceId', '==', workspaceId),
      where('formId', '==', formId),
      orderBy('submittedAt', 'desc'),
      limit(100),
    )
    return onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as FormResponse)
        responsesByForm.set(formId, docs)
        emit()
      },
      handleError,
    )
  })

  return () => {
    for (const unsub of unsubs) unsub()
  }
}

export { canBindStockItem, validateCanBindStockItem, type CanBindStockItemValidation } from './forms/formStockBinding'

